/**
 * What the organisation's status and plan mean, in one place so the top-bar
 * badges and Organisation say the same thing.
 *
 * PENDING — transcribed from the server, not assumed. A new org is created
 * `pending` (routes/partner/orgs.ts) and an operator moves it to `active`
 * (POST /admin/ops/partners/:id/approve). Nothing on the lead path reads the
 * status: `GET /widget/config` and lead intake (routes/widget.ts) join
 * `partners` for the name only, routing and delivery (jobs/partner/*) never
 * look at it, and agent tokens refuse only `suspended`
 * (services/partner-agent-tokens.ts). The one thing that waits is search
 * indexing of the hosted page: `indexable = published && status === 'active'`
 * (routes/hosted-page.ts); the page itself is served (isHostedPageServed
 * refuses only `suspended`). No review time is promised because none exists
 * in the code or the spec.
 *
 * PILOT — the plan is not enforced in code; what it includes is the pricing
 * decision in specs/012-widget-partner-portal/decisions.md §1 ("Pilot: 750
 * diagnoses or 60 days; price for the next plan named at day 45"). The price
 * is left out because it varies by agreement.
 */
import type { Org } from '../api/types';
import { can } from '../auth/roles';
import { useOptionalSession } from '../auth/session';
import { Link } from '../router';

export const PLAN_LABELS: Record<string, string> = {
  pilot: 'Pilot',
  payg: 'Pay as you go',
  flat: 'Flat',
  enterprise: 'Enterprise',
};

export function planLabel(plan: string | null | undefined): string {
  if (!plan) return 'Plan';
  return PLAN_LABELS[plan] ?? plan.charAt(0).toUpperCase() + plan.slice(1);
}

export const PENDING_TITLE = 'Pending approval';

export function PendingExplanation() {
  return (
    <div className="stack-sm">
      <p style={{ margin: 0 }}>We’re reviewing your organisation. A FinderlyFix operator approves every new one; there’s nothing you need to do.</p>
      <p style={{ margin: 0 }}>Meanwhile:</p>
      <ul className="stack-sm" style={{ margin: 0, paddingLeft: 18 }}>
        <li>Your widget works on your website, with your published settings.</li>
        <li>Live leads are captured and delivered to your destinations and contractors as usual — nothing is held back.</li>
        <li>If you use a FinderlyFix-hosted page, it is live, but search engines are asked not to index it until you’re approved.</li>
      </ul>
    </div>
  );
}

/**
 * Contractor billing (phase 2, `whoami.features.contractor_billing`): fees,
 * the statement and invoices live on `/billing`, so the plan text points
 * there instead of saying billing isn't in the portal.
 */
function BillingPointer() {
  const session = useOptionalSession();
  const canSee = can(session?.role ?? null, 'billing');
  return canSee ? (
    <>
      Fees, your statement and invoices are on the <Link to="/billing">Billing</Link> page.
    </>
  ) : (
    <>Fees, the statement and invoices are on the Billing page, for owners, admins and billing members.</>
  );
}

export function PlanExplanation({ plan }: { plan: string | null | undefined }) {
  const session = useOptionalSession();
  const billingOn = session?.whoami?.features?.contractor_billing === true;
  if (plan === 'pilot') {
    return (
      <div className="stack-sm">
        <p style={{ margin: 0 }}>
          The pilot runs for 60 days or 750 completed diagnoses, whichever comes first. Around day 45 you review your deflection numbers with
          FinderlyFix and agree the plan that follows.
        </p>
        <p style={{ margin: 0 }}>{billingOn ? <BillingPointer /> : 'Billing isn’t in the portal yet: your FinderlyFix contact handles it.'}</p>
      </div>
    );
  }
  if (billingOn) {
    return (
      <p style={{ margin: 0 }}>
        Plan changes are handled by your FinderlyFix contact. <BillingPointer />
      </p>
    );
  }
  return <p style={{ margin: 0 }}>Billing and plan changes are handled by your FinderlyFix contact.</p>;
}

export function statusLabel(status: Org['status'] | undefined): string {
  if (status === 'pending') return PENDING_TITLE;
  if (status === 'active') return 'Active';
  if (status === 'suspended') return 'Suspended';
  return status ? status.charAt(0).toUpperCase() + status.slice(1) : '—';
}
